import { useQuery } from "@tanstack/react-query";
import { Divider, Stack, Typography } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import {
  complianceHistoryQueryOptions,
  partnerByIdQueryOptions,
  type OverallAssessmentCommentHistoryValue,
  type OverallAssessmentHistoryValue,
  type SectionEvaluationHistoryValue,
} from "../../api/partners";
import { logUnhandled } from "../../api/logError";
import { DynamicGrid } from "../../dynamicGrid";
import { DynamicHistory } from "../../dynamicHistory";
import { Drawer } from "../../shared/Drawer";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { PartnerDrawerHeader } from "./PartnerDrawerHeader";
import { EvaluationDot } from "./ComplianceSubDrawer";

export const ComplianceManuelHistoryDrawer = () => {
  const partnerId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const partnerByIdQuery = useQuery(partnerByIdQueryOptions(partnerId));

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!partnerByIdQuery.data) return <Drawer.Loading />;

        const partner = partnerByIdQuery.data;

        return (
          <>
            <PartnerDrawerHeader status="hidden" partner={partner} onClose={() => navigate(paths.partnersGrid())} />
            <Drawer.SubDrawerHeader
              title={t("Compliance.History.SubDrawer.Header.Title")}
              onBackClick={() => navigate("..", { relative: "path" })}
            />
            <Divider />
            <ComplianceHistoryEntries complianceId={partner.compliance.id} />
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

const ComplianceHistoryEntries = ({ complianceId }: { complianceId: string }) => {
  const { t } = useTranslation();
  const historyQuery = useQuery(complianceHistoryQueryOptions(complianceId));

  if (!historyQuery.data) return <Drawer.Loading />;

  return (
    <DynamicHistory.Entries
      historyEntries={historyQuery.data.history}
      renderCustomEntry={entry => {
        switch (entry.customType) {
          case "overallAssessment":
            return (
              <DynamicHistory.BeforeAndAfterEntry
                {...entry}
                header={t("Compliance.Subdrawer.Evaluation.OverallAssessment")}
                before={<OverallAssessmentValue value={entry.oldValue} />}
                after={<OverallAssessmentValue value={entry.newValue} />}
              />
            );
          case "overallComment":
            return (
              <DynamicHistory.BeforeAndAfterEntry
                {...entry}
                header={t("Compliance.History.Entry.OverallAssessmentComment")}
                before={<OverallAssessmentCommentValue value={entry.oldValue} />}
                after={<OverallAssessmentCommentValue value={entry.newValue} />}
              />
            );
          case "sectionEvaluation":
            return (
              <DynamicHistory.BeforeAndAfterEntry
                {...entry}
                header={entry.sectionHeader}
                before={<SectionEvaluationValue value={entry.oldValue} />}
                after={<SectionEvaluationValue value={entry.newValue} />}
              />
            );
          case "partnerComplianceAssessmentResponded":
            return (
              <PartnerComplianceAssessmentRespondedEntry createdAt={entry.createdAt} createdBy={entry.createdBy} />
            );
          default:
            return logUnhandled(entry, "custom entry");
        }
      }}
    />
  );
};

const OverallAssessmentValue = ({ value }: { value: OverallAssessmentHistoryValue }) => {
  const { t } = useTranslation();

  if (!value) return <DynamicHistory.EmptyValue />;

  return (
    <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
      <EvaluationDot evaluation={value} />
      <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(14) }}>
        {t(`Compliance.Subdrawer.Evaluation.${value}`)}
      </Typography>
    </Stack>
  );
};

const OverallAssessmentCommentValue = ({ value }: { value: OverallAssessmentCommentHistoryValue }) => {
  if (!value) return <DynamicHistory.EmptyValue />;

  return <DynamicHistory.SnapshotValueText>{value}</DynamicHistory.SnapshotValueText>;
};

const SectionEvaluationValue = ({ value }: { value: SectionEvaluationHistoryValue }) => {
  if (!value) return <DynamicHistory.EmptyValue />;

  if (!value.assessment && !value.comment) return <DynamicHistory.EmptyValue />;

  return (
    <Stack sx={{ gap: 1 }}>
      {value.assessment && <OverallAssessmentValue value={value.assessment} />}
      {value.comment && (
        <Typography
          sx={{
            fontWeight: "400",
            fontSize: theme => theme.typography.pxToRem(14),
            color: theme => theme.palette.text.tertiary,
          }}
        >
          {value.comment}
        </Typography>
      )}
    </Stack>
  );
};

const PartnerComplianceAssessmentRespondedEntry = ({
  createdAt,
  createdBy,
}: {
  createdAt: string;
  createdBy: { firstName: string; lastName: string; email: string };
}) => {
  const { t, formatDate } = useTranslation();
  return (
    <Stack>
      <Typography
        sx={{
          fontWeight: "400",
          fontSize: theme => theme.typography.pxToRem(12),
          mb: 0.5,
          color: theme => theme.palette.text.tertiary,
        }}
      >
        {formatDate(createdAt, { showTime: true })}
      </Typography>
      <Typography sx={{ fontWeight: "600", fontSize: theme => theme.typography.pxToRem(14) }}>
        {t("Compliance.History.PartnerComplianceAssessmentResponded.ReceivedFrom")}{" "}
        <Typography component="span" sx={{ fontWeight: "400", fontSize: theme => theme.typography.pxToRem(14) }}>
          {createdBy.firstName} {createdBy.lastName}
        </Typography>{" "}
        <Typography
          component="span"
          sx={{ fontSize: theme => theme.typography.pxToRem(14), color: theme => theme.palette.text.tertiary }}
        >
          ({createdBy.email})
        </Typography>
      </Typography>
    </Stack>
  );
};
